import { AFTERNOON_OUT_KEY, FULL_DAY_KEY, MORNING_OUT_KEY } from "shared/constants/timeOff";
import {
    addDays,
    eachDayOfInterval,
    getYear,
    isAfter,
    isBefore,
    isEqual,
    isSameDay,
    isWeekend,
    parseISO,
    startOfDay,
} from 'date-fns';

import moment from "moment";

export function getDayOption(morning, afternoon) {
    if (morning && afternoon) {
        return FULL_DAY_KEY;
    }
    if (morning) {
        return MORNING_OUT_KEY;
    }
    return AFTERNOON_OUT_KEY;
}

export function areDatesWithSameYear(date1, date2) {
    const date1Parsed = parseDate(date1);
    const date2Parsed = parseDate(date2);

    const year1 = getYear(date1Parsed);
    const year2 = getYear(date2Parsed);

    return year1 === year2;
}

export function transformTimeOffDataToFormData(timeOffData) {
    if (!timeOffData) {
        return {
            OOOType: undefined,
            date: {
                start: null,
                end: null,
            },
            firstDayOptions: FULL_DAY_KEY,
            lastDayOptions: FULL_DAY_KEY,
            note: "",
        };
    }
    return {
        OOOType: timeOffData.reasonId,
        date: {
            start: moment(timeOffData.firstDayOut).format("YYYY-MM-DD"),
            end: moment(timeOffData.lastDayOut).format("YYYY-MM-DD"),
        },
        firstDayOptions: getDayOption(
            timeOffData.firstDayOutMorning,
            timeOffData.firstDayOutAfternoon
        ),
        lastDayOptions: getDayOption(
            timeOffData.lastDayOutMorning,
            timeOffData.lastDayOutAfternoon
        ),
        note: timeOffData?.note,
    };
}

export function parseDate(date) {
    let result = null;

    if (date == null) {
        return null;
    }

    if (date instanceof Date) {
        return date;
    }

    if (typeof date === "string") {
        return parseISO(date);
    }

    result = parseDatePickerDate(date);
    return result;
}

export function parseDatePickerDate(date) {
    let result = null;

    if (!date || date == null) {
        return null;
    }

    const year = date?.year;
    const month = date?.month - 1;
    const day = date?.day;

    result = new Date(year, month, day);

    return result;
}

export function formDataToOOOData(
    userId,
    approverId,
    vacationRequestId,
    oooOptions,
    agencyId,
    formData = {}
) {
    const newOutOfOfficeData = {};
    newOutOfOfficeData.reasonId = formData.OOOType;
    newOutOfOfficeData.firstDayOptions = formData.firstDayOptions;
    newOutOfOfficeData.lastDayOptions = formData.lastDayOptions;
    newOutOfOfficeData.note = formData.note;

    const start = formData?.date?.start;
    const end = formData?.date?.end;
    newOutOfOfficeData.firstDayOut = parseDate(start);
    newOutOfOfficeData.lastDayOut = parseDate(end);
    newOutOfOfficeData.userId = userId;
    newOutOfOfficeData.agencyId = agencyId;
    newOutOfOfficeData.approver = approverId;
    newOutOfOfficeData.id = vacationRequestId;
    newOutOfOfficeData.reasonName = oooOptions?.find(
        (x) => x?.value === formData.OOOType
    )?.label;
    return newOutOfOfficeData;
}

export const getDropdownOptions = (optionDetails = [], Id = "Id", Name = "Name") => {
    return optionDetails.reduce((acc, val) => {
        return [
            ...acc,
            {
                value: val[Id],
                label: val[Name],
            },
        ];
    }, []);
};

export const calculateHalfDaySelection = (startDate, endDate, firstDayOption, secondDayOption) => {
    if (isSameDay(startDate, endDate)) {
        return firstDayOption === FULL_DAY_KEY ? 0 : 0.5;
    }

    const firstDayDeduction = firstDayOption === FULL_DAY_KEY ? 0 : 0.5;
    const secondDayDeduction = secondDayOption === FULL_DAY_KEY ? 0 : 0.5;

    return firstDayDeduction + secondDayDeduction;
};

/**
 * Calculates the number of business days between two dates, excluding weekends and holidays
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date  
 * @param {Array} holidays - Array of holiday dates
 * @returns {number} Number of business days
 */
export const getBusinessDaysCount = (startDate, endDate, holidays = []) => {
    const allDays = eachDayOfInterval({ start: startDate, end: endDate });

    const filteredDays = allDays.filter(day => {
        if (isWeekend(day)) {
            return false;
        }

        const isHoliday = holidays.some(holiday => isSameDay(holiday, day));
        return !isHoliday;
    });

    const result = filteredDays.length;
    return result;
};

/**
 * Generates an array of dates from time-off requests to disable in the calendar picker.
 * Excludes half-day boundaries (first/last day) to allow overlapping half-day requests.
 * Only includes time-offs from 2 months ago forward.
 * 
 * @param {Array} timeOffRequests - Array of time-off request objects
 * @param {Date} minDate - Minimum date to include (typically 2 months ago)
 * @returns {Date[]} Array of dates to disable in the calendar
 */
export function getDisabledTimeOffDates(timeOffRequests, minDate) {
    if (!timeOffRequests || timeOffRequests.length === 0) {
        return [];
    }

    const result = timeOffRequests.flatMap(timeOff => {
        let firstDay = parseDate(timeOff.firstDayOut);
        let lastDay = parseDate(timeOff.lastDayOut);

        // Skip if the time-off ends before the minimum date
        if (isBefore(lastDay, minDate)) {
            return [];
        }

        const isSingleDay = isSameDay(firstDay, lastDay);
        const isFirstDayHalf = !timeOff.firstDayOutMorning || !timeOff.firstDayOutAfternoon;
        const isLastDayHalf = !timeOff.lastDayOutMorning || !timeOff.lastDayOutAfternoon;

        // Adjust boundaries for half-days
        if (!isSingleDay && isLastDayHalf) {
            lastDay = addDays(lastDay, -1);
        }

        if (isFirstDayHalf) {
            firstDay = addDays(firstDay, 1);
        }

        // If after adjustments the range is invalid, return empty array
        if (isAfter(firstDay, lastDay)) {
            return [];
        }

        // Generate all dates in the range
        return eachDayOfInterval({ start: firstDay, end: lastDay });
    });

    return result;
}

/**
 * Checks if two date ranges overlap
 * @param {Date} start1 - Start date of first range
 * @param {Date} end1 - End date of first range
 * @param {Date} start2 - Start date of second range
 * @param {Date} end2 - End date of second range
 * @returns {boolean} True if ranges overlap
 */
export const doDateRangesOverlap = (start1, end1, start2, end2) => {
    // Clean dates to compare only date part (ignore time)
    const cleanStart1 = startOfDay(start1);
    const cleanEnd1 = startOfDay(end1);
    const cleanStart2 = startOfDay(start2);
    const cleanEnd2 = startOfDay(end2);

    // Check all overlap conditions:
    // 1. start2 is within range1: start1 <= start2 <= end1
    // 2. end2 is within range1: start1 <= end2 <= end1
    // 3. range2 contains range1: start2 <= start1 && end2 >= end1
    // 4. range1 contains range2: start1 <= start2 && end1 >= end2

    return (
        (isBefore(cleanStart2, cleanEnd1) || isEqual(cleanStart2, cleanEnd1)) &&
        (isAfter(cleanEnd2, cleanStart1) || isEqual(cleanEnd2, cleanStart1))
    );
};

/**
 * Filters time-off requests that overlap with given date range
 * @param {Array} timeOffRequests - Array of time-off request objects
 * @param {Date} startDate - Start date to check
 * @param {Date} endDate - End date to check
 * @returns {Array} Array of overlapping time-off requests
 */
export const getOverlappingTimeOffs = (timeOffRequests, startDate, endDate) => {
    if (!timeOffRequests || timeOffRequests.length === 0) {
        return [];
    }

    const result = timeOffRequests.filter((timeOff) => {
        const timeOffStart = parseDate(timeOff.firstDayOut);
        const timeOffEnd = parseDate(timeOff.lastDayOut);

        return doDateRangesOverlap(startDate, endDate, timeOffStart, timeOffEnd);
    });

    return result;
};

export const checkIfHalfDaysOverlapsOK = (overlaps, startDate, endDate, firstDayOption, lastDayOption) => {
    if (!overlaps?.length) {
        return [];
    }

    // Helper: Check if two half-day options are complementary (don't conflict)
    const areComplementaryHalfDays = (existingMorning, existingAfternoon, newRequest) => {
        // New request is morning out, existing is afternoon out only
        if (newRequest === "MorningOut" && !existingMorning && existingAfternoon) {
            return true;
        }
        // New request is afternoon out, existing is morning out only
        if (newRequest === "AfternoonOut" && existingMorning && !existingAfternoon) {
            return true;
        }
        return false;
    };

    // Helper: Check if a time-off day is a half-day (only one part out)
    const isHalfDay = (morning, afternoon) => {
        return (morning && !afternoon) || (!morning && afternoon);
    };

    const allowedOverlaps = overlaps.filter(timeOff => {
        const existingStart = parseDate(timeOff.firstDayOut);
        const existingEnd = parseDate(timeOff.lastDayOut);
        const isSingleDayExisting = isSameDay(existingStart, existingEnd);
        const isSingleDayNew = isSameDay(startDate, endDate);

        // Scenario 1: Same single day with complementary half-days
        if ((isSingleDayExisting || isSingleDayNew) && isSameDay(startDate, existingStart)) {
            const isNewHalfDay = firstDayOption !== FULL_DAY_KEY;
            const isExistingHalfDay = isHalfDay(
                timeOff.firstDayOutMorning,
                timeOff.firstDayOutAfternoon
            );

            if (isNewHalfDay && isExistingHalfDay) {
                return areComplementaryHalfDays(
                    timeOff.firstDayOutMorning,
                    timeOff.firstDayOutAfternoon,
                    firstDayOption
                );
            }
        }

        // Scenario 2: New start date matches existing end date (complementary half-days)
        if (isSameDay(startDate, existingEnd)) {
            const isNewHalfDay = firstDayOption !== FULL_DAY_KEY;
            const isExistingHalfDay = isHalfDay(
                timeOff.lastDayOutMorning,
                timeOff.lastDayOutAfternoon
            );

            if (isNewHalfDay && isExistingHalfDay) {
                return areComplementaryHalfDays(
                    timeOff.lastDayOutMorning,
                    timeOff.lastDayOutAfternoon,
                    firstDayOption
                );
            }
        }

        // Scenario 3: New end date matches existing start date (complementary half-days)
        if (isSameDay(endDate, existingStart) && lastDayOption) {
            const isNewHalfDay = lastDayOption !== FULL_DAY_KEY;
            const isExistingHalfDay = isHalfDay(
                timeOff.firstDayOutMorning,
                timeOff.firstDayOutAfternoon
            );

            if (isNewHalfDay && isExistingHalfDay) {
                return areComplementaryHalfDays(
                    timeOff.firstDayOutMorning,
                    timeOff.firstDayOutAfternoon,
                    lastDayOption
                );
            }
        }

        return false;
    });

    return allowedOverlaps.map(x => x.id);
};

export const checkIfHalfDaysOverlapsWrong = (overlaps, startDate, endDate, firstDayOption, lastDayOption) => {
    if (!overlaps?.length) {
        return false;
    }

    // Helper: Check if two options conflict on the same half-day
    const doHalfDaysConflict = (existingMorning, existingAfternoon, newRequest) => {
        // New request is full day - always conflicts if existing has any time off
        if (newRequest === FULL_DAY_KEY) {
            return existingMorning || existingAfternoon;
        }

        // Existing is full day - conflicts with any new request
        if (existingMorning && existingAfternoon) {
            return true;
        }

        // Both use same half (morning or afternoon)
        if (newRequest === "MorningOut" && existingMorning) {
            return true;
        }
        if (newRequest === "AfternoonOut" && existingAfternoon) {
            return true;
        }

        return false;
    };

    return overlaps.some(timeOff => {
        const existingStart = parseDate(timeOff.firstDayOut);
        const existingEnd = parseDate(timeOff.lastDayOut);
        const isSingleDayExisting = isSameDay(existingStart, existingEnd);
        const isSingleDayNew = isSameDay(startDate, endDate);

        // Scenario 1: Same day with conflicting half-days or full day
        if ((isSingleDayExisting || isSingleDayNew) && isSameDay(startDate, existingStart)) {
            return doHalfDaysConflict(
                timeOff.firstDayOutMorning,
                timeOff.firstDayOutAfternoon,
                firstDayOption
            );
        }

        // Scenario 2: New start date matches existing end date (with conflict)
        if (isSameDay(startDate, existingEnd)) {
            return doHalfDaysConflict(
                timeOff.lastDayOutMorning,
                timeOff.lastDayOutAfternoon,
                firstDayOption
            );
        }

        // Scenario 3: New end date matches existing start date (with conflict)
        if (isSameDay(endDate, existingStart) && lastDayOption) {
            return doHalfDaysConflict(
                timeOff.firstDayOutMorning,
                timeOff.firstDayOutAfternoon,
                lastDayOption
            );
        }

        return false;
    });
};
